import { useRef, useState } from 'react';
import { chakra, Box, Button } from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { AxiosError } from 'axios';
import queryString from 'query-string';

import { routes } from 'app/routing/routesConfig';
import { useAppDispatch, useAppSelector } from 'app/store';
import { addAlertAction } from 'app/store/Alerts/slice';
import { signUpVerifiedAction } from 'app/store/Mixpanel/slice';
import {
  getAuthenticatedSelector,
  getConfirmSignUpLink,
} from 'app/store/Auth/selectors';
import { signOutEffect } from 'app/store/Auth/effects';
import {
  apiErrorHandler,
  getAppStoreUrl,
  getMobileOperatingSystem,
} from 'app/utils/utils';
import {
  useFetch,
  useFetchValidationSchema,
  useIsMobile,
} from 'app/utils/hooks';
import { ResendCode } from 'app/components/ResendCode';
import { registerAppsFlyerSignUpEvent } from 'app/utils/appsFlyer';
import {
  ConfirmFormSchema,
  Form,
  InvalidTokenMessage,
} from 'app/views/ConfirmAccount/utils';
import { ChakraAuthContentWrapper, TokenInput } from 'app/components/Core';
import { useReferralParam } from 'app/components/SignUp/SignUpForm/hooks';
import { AuthFormFooter, AuthFormHeader } from 'app/components/AuthForm';

export const ConfirmAccount = () => {
  const confirmLink = useAppSelector(getConfirmSignUpLink);
  const isAuthenticated = useAppSelector(getAuthenticatedSelector);
  const dispatch = useAppDispatch();
  const mobileOperatingSystem = getMobileOperatingSystem();
  const isMobile = useIsMobile();

  const { callAPI } = useFetch<{
    confirmationCode: string;
    clientId: string;
    username: string;
  }>();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    confirmationCode = '',
    username = '',
    clientId = '',
  } = queryString.parse(search);

  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
  } = useForm<Form>({
    defaultValues: { token: confirmationCode as string },
    mode: 'all',
    resolver: yupResolver(ConfirmFormSchema),
  });
  const [tokenError, setTokenError] = useState('');

  const redirectToLogin = () => {
    if (isAuthenticated) {
      dispatch(signOutEffect({ pathname }));
    } else {
      navigate(routes.auth.path);
    }
  };

  const {
    validationSchema: referralValidationSchema,
  } = useFetchValidationSchema({ link: confirmLink });

  const referral = useRef<string>();
  useReferralParam({ referralValidationSchema, referral });

  const onSubmit = handleSubmit(async ({ token }) => {
    if (!confirmLink || !username || !clientId) return;

    try {
      await callAPI(confirmLink.href, {
        confirmationCode: token,
        username: username.toString(),
        clientId: clientId.toString(),
      });
      registerAppsFlyerSignUpEvent();

      dispatch(signUpVerifiedAction());
      if (isMobile) {
        const appStoreUrl = getAppStoreUrl(mobileOperatingSystem);
        if (appStoreUrl) {
          window.location.replace(appStoreUrl);
          return;
        }
        navigate(routes.confirmAccountSuccess.path);
        return;
      }
      redirectToLogin();
    } catch (e: any) {
      const { error = {} } = (e as AxiosError<any>).response?.data;
      if (error?.statusCode === 400) {
        setTokenError(InvalidTokenMessage);
      } else {
        dispatch(addAlertAction({ message: apiErrorHandler(e.response) }));
      }
    }
  });

  if (!username || !clientId) {
    navigate(routes.pageNotFound.path, { replace: true });
    return null;
  }

  return (
    <ChakraAuthContentWrapper mt={12}>
      <AuthFormHeader heading="Email verification" w="full">
        Please use the code we&apos;ve
        <br />
        emailed to you
      </AuthFormHeader>
      <chakra.form onSubmit={onSubmit} w="full" mt={10}>
        <Controller
          control={control}
          name="token"
          render={({ field: { onChange } }) => (
            <TokenInput
              onChange={(val) => {
                if (tokenError) setTokenError('');
                onChange(val);
              }}
              defaultValue={confirmationCode?.toString()}
              error={tokenError}
            />
          )}
        />
        <Button
          type="submit"
          isDisabled={!isValid || isSubmitting || Boolean(tokenError)}
          w="full"
          mt={26}
          data-step="verify"
        >
          Verify
        </Button>
        <Box width="full" my={12}>
          <ResendCode username={username as string} justifyContent="center" />
        </Box>
        <AuthFormFooter>
          Have an account?
          <Button
            variant="link"
            color="M.70"
            fontWeight={400}
            textDecoration="underline"
            letterSpacing={0}
            aria-label="sign in"
            onClick={() => redirectToLogin()}
          >
            Sign In
          </Button>
        </AuthFormFooter>
      </chakra.form>
    </ChakraAuthContentWrapper>
  );
};
